// Phase A: lossless "current editor -> canonical flow draft" extractor.
//
// The canonical draft schema is the same one consumed by
// applyFlowDraftToEditor / src/agent/editor-apply.js:
//   { flowDraftVersion, summary, axes:[{name,motions:[{id,name,...}]}],
//     nodes:[{id,type,...,x,y}], connections:[{from,to,output,input}] }
//
// Invariant that fixes the silent-drop bug (multi-axis-timing-chart.html
// motion branch): every emitted motion node carries axisName + motionId
// that resolve against axes[].motions[] (matched by id, axisName by name),
// so buildRuntimeAxesFromDraft + buildDrawflowNodeSpecs always produce a
// non-empty runtimeMotionId. Phase A tests assert this directly.

function numOr(value, fallback) {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
}

function defaultGetNodeData(node) {
    return node?.data || {};
}

function defaultGetNodeTypeFromData(data) {
    return data?.nodeType || (data?.motionId ? "motion" : "");
}

function normalizeConditionEntry(entry = {}, index = 0, fallbackName = "条件") {
    const baseLabel = index === 0 ? fallbackName : `条件${index + 1}`;
    const label = String(entry?.label || baseLabel).trim() || baseLabel;
    const expression = String(entry?.expression || label).trim() || label;
    return { label, expression };
}

function normalizeConditionEntries(entries = [], fallbackName = "条件", mode = "single") {
    const source = Array.isArray(entries) ? entries : [];
    const normalized = source
        .map((entry, index) => normalizeConditionEntry(entry, index, fallbackName))
        .filter((entry) => entry.label || entry.expression);
    if (!normalized.length) {
        normalized.push(normalizeConditionEntry({ label: fallbackName, expression: fallbackName }, 0, fallbackName));
    }
    return mode === "composite" ? normalized : [normalized[0]];
}

export function buildFlowDraftFromCurrentEditor(drawflowExport, project, helpers = {}) {
    const getNodeData = helpers.getNodeData || defaultGetNodeData;
    const getNodeTypeFromData = helpers.getNodeTypeFromData || defaultGetNodeTypeFromData;
    const home = drawflowExport?.drawflow?.Home?.data || {};
    const axesSrc = Array.isArray(project?.axes) ? project.axes : [];

    const axes = axesSrc.map((axis) => ({
        name: String(axis?.name || ""),
        motions: (Array.isArray(axis?.motions) ? axis.motions : []).map((m) => ({
            id: String(m?.id || ""),
            name: String(m?.name || ""),
            distance: numOr(m?.distance, 100),
            accel: numOr(m?.accel, 1),
            decel: numOr(m?.decel, 1),
            maxSpeed: numOr(m?.maxSpeed, 500),
            waitTime: numOr(m?.waitTime, 0)
        }))
    }));

    const motionById = new Map();
    for (const axis of axesSrc) {
        for (const motion of Array.isArray(axis?.motions) ? axis.motions : []) {
            motionById.set(String(motion?.id), { axis, motion });
        }
    }

    const nodes = [];
    const connections = [];

    for (const rawId of Object.keys(home)) {
        const node = home[rawId];
        const data = getNodeData(node);
        const type = getNodeTypeFromData(data);
        const id = String(node?.id ?? rawId);
        const x = numOr(node?.pos_x, 0);
        const y = numOr(node?.pos_y, 0);

        if (type === "start") {
            nodes.push({ id, type, label: data.label || "開始", x, y });
        } else if (type === "button") {
            nodes.push({
                id,
                type,
                label: data.label || "押しボタン",
                signalName: String(data.signalName || "X_PB1").trim() || "X_PB1",
                mode: data.mode === "alternate" ? "alternate" : "momentary",
                trueLabel: data.trueLabel || "ON",
                falseLabel: data.falseLabel || "OFF",
                x,
                y
            });
        } else if (type === "condition") {
            const conditionText = String(data.conditionText || "条件").trim() || "条件";
            const conditionMode = data.conditionMode === "composite" ? "composite" : "single";
            nodes.push({
                id,
                type,
                conditionText,
                conditionMode,
                operator: data.operator === "OR" ? "OR" : "AND",
                conditions: normalizeConditionEntries(data.conditions, conditionText, conditionMode),
                trueLabel: data.trueLabel || "ON",
                falseLabel: data.falseLabel || "OFF",
                timeoutLabel: data.timeoutLabel || "TIMEOUT",
                timeoutSec: numOr(data.timeoutSec, 5),
                x,
                y
            });
        } else if (type === "error") {
            nodes.push({
                id,
                type,
                errorCode: data.errorCode || "E001",
                errorMessage: data.errorMessage || "エラー",
                x,
                y
            });
        } else if (type === "signal_output") {
            nodes.push({
                id,
                type,
                label: data.label || "信号出力",
                signalScope: data.signalScope === "internal_flag" ? "internal_flag" : "external",
                signalName: data.signalName || "Signal",
                action: data.action || "ON",
                x,
                y
            });
        } else if (type === "wait") {
            nodes.push({
                id,
                type,
                label: data.label || "待機",
                durationSec: numOr(data.durationSec, 1),
                x,
                y
            });
        } else if (type === "subprocess") {
            nodes.push({
                id,
                type,
                label: data.label || data.subprocessName || "サブプロセス",
                subprocessId: data.subprocessId || "",
                subprocessName: data.subprocessName || data.label || "サブプロセス",
                durationSec: numOr(data.durationSec, 1),
                timingMode: data.timingMode || "fixed_duration",
                x,
                y
            });
        } else if (type === "end") {
            nodes.push({
                id,
                type,
                label: data.label || "終了",
                result: data.result || "normal",
                x,
                y
            });
        } else if (type === "motion") {
            const resolved =
                (data.motionId && motionById.get(String(data.motionId))) ||
                (helpers.resolveMotionByNodeId && helpers.resolveMotionByNodeId(id)) ||
                null;
            const axisName = resolved?.axis ? String(resolved.axis.name || "") : String(data.axisName || "");
            const motionId = resolved?.motion ? String(resolved.motion.id || "") : String(data.motionId || "");
            const motionName = resolved?.motion ? String(resolved.motion.name || "") : String(data.motionName || "");
            nodes.push({ id, type, axisName, motionId, motionName, x, y });
        } else {
            nodes.push({ id, type: type || "node", label: data.label || "", x, y });
        }

        const outputs = node?.outputs || {};
        for (const outName of Object.keys(outputs)) {
            const conns = outputs[outName]?.connections || [];
            for (const conn of conns) {
                if (!conn || conn.node == null) continue;
                connections.push({
                    from: id,
                    to: String(conn.node),
                    output: outName,
                    // Drawflow export stores the destination input port name in
                    // the connection's `output` field on the source side.
                    input: conn.output || "input_1"
                });
            }
        }
    }

    return {
        flowDraftVersion: "1.0",
        summary: "現在のフローを抽出しました。",
        axes,
        nodes,
        connections
    };
}
